import type { ChangeEvent } from 'react'

import type { Theme } from '@emotion/react'
import Cleave from 'cleave.js/react'
import { isNil } from 'ramda'
import type { UseFormReturn } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'

import { useIsTouchScreen } from '@twisto/hooks'
import { normalizeReactNode } from '@twisto/utils'

import { useFocus } from './focus.hook'
import type { InputProps } from './input-common'
import * as styles from './input.styles'

type Props = InputProps

export const Input = ({
  border = true,
  type = 'text',
  value,
  name,
  className = '',
  startAdornment,
  endAdornment,
  styles: stylesProp = {},
  nativeInputProps,
  formattedOptions,
  error: errorProp = false,
  focused: focusedProp = false,
  uppercased,
  onBlur,
  onFocus,
  onChange: onChangeProp,
  rules = {},
  autoFocus,
  ...props
}: Props) => {
  const { control, register, formState } =
    (useFormContext() as UseFormReturn | null) || {}
  const fieldErrors = formState?.errors ?? {}
  const hasError = errorProp || Boolean(fieldErrors[name])
  const isTouchSreen = useIsTouchScreen()

  const { isFocused, handleBlur, handleFocus } = useFocus({
    defaultValue: focusedProp,
    onBlur,
    onFocus,
  })

  const rootStyles = (theme: Theme) => [
    styles.root(theme),
    styles.typographyStyles,
    !border && styles.borderLess,
    isFocused && styles.focused(theme),
    hasError && styles.error(theme),
    stylesProp.root,
  ]

  const inputStyles = (theme: Theme) => [
    styles.input(theme),
    styles.typographyStyles,
    stylesProp.input,
  ]

  const startAdornmentStyles = (theme: Theme, disabled = false) => [
    styles.adornment(theme, disabled),
    styles.startAdornment,
    styles.typographyStyles,
    stylesProp.startAdornment,
  ]

  const endAdornmentStyles = (theme: Theme, disabled = false) => [
    styles.adornment(theme, disabled),
    styles.endAdornment,
    styles.typographyStyles,
    stylesProp.endAdornment,
  ]

  const mergedNativeInputProps = {
    ...nativeInputProps,
    ...props,
    name,
    autoFocus: !isTouchSreen ? autoFocus : false,
    'data-testid': `input${hasError ? '-error' : ''}`,
  }

  const transformInputValue = (inputValue?: string | number) =>
    !isNil(inputValue) && (uppercased ?? false)
      ? inputValue.toString().toUpperCase()
      : inputValue

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const transformedValue = transformInputValue(event.target.value)

    if (transformedValue !== undefined) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = transformedValue.toString()
    }
    onChangeProp && onChangeProp(event)
  }

  const formProps = register?.(name, {
    ...rules,
    onBlur: handleBlur,
    onChange: handleChange,
    value,
  })

  const getInputComponent = () => {
    if (formattedOptions && control) {
      return (
        <Controller
          control={control}
          defaultValue={value}
          name={name}
          render={({
            field: {
              onChange,
              onBlur: controllerOnBlur,
              value: controllerValue,
            },
          }) => (
            <Cleave
              css={inputStyles}
              options={formattedOptions}
              type={type}
              onBlur={(e) => {
                handleBlur(e)
                controllerOnBlur()
              }}
              onInit={(instance) => {
                // fix for Cleave issue https://github.com/nosir/cleave.js/issues/601#issuecomment-902747682
                /* eslint-disable no-param-reassign */
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ;(instance as any).lastInputValue = ''
                /* eslint-enable no-param-reassign */
              }}
              {...mergedNativeInputProps}
              value={transformInputValue(
                controllerValue ?? mergedNativeInputProps.defaultValue ?? ''
              )}
              onChange={(e) => {
                handleChange(e)
                onChange(e)
              }}
              onFocus={handleFocus}
            />
          )}
          rules={rules}
        />
      )
    }

    if (formattedOptions && !control) {
      return (
        <Cleave
          css={inputStyles}
          options={formattedOptions}
          type={type}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          {...mergedNativeInputProps}
          value={transformInputValue(
            value ?? mergedNativeInputProps.defaultValue
          )}
          {...formProps}
        />
      )
    }

    return (
      <input
        css={inputStyles}
        type={type}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        {...mergedNativeInputProps}
        defaultValue={
          value === undefined
            ? transformInputValue(mergedNativeInputProps.defaultValue)
            : undefined
        }
        value={value !== undefined ? transformInputValue(value) : undefined}
        {...formProps}
      />
    )
  }

  return (
    <div className={className} css={rootStyles}>
      {normalizeReactNode(startAdornment, {
        css: (theme: Theme) =>
          startAdornmentStyles(theme, mergedNativeInputProps.disabled),
      })}

      {getInputComponent()}

      {normalizeReactNode(endAdornment, {
        css: (theme: Theme) =>
          endAdornmentStyles(theme, mergedNativeInputProps.disabled),
      })}
    </div>
  )
}
